import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  getUserItemPreferences,
  getMasterEquipmentDefinitions,
  getMasterEquipment,
  getUserEquipment,
  updateUserItemPreference,
  createUserItemPreference,
  createUserEquipment,
  uploadUserEquipmentImage,
  getUnreadItemTypes,
  getUnreadItemTypesByUserId,
} from '../services/api';
import EquipmentFilter from './EquipmentFilter';
import EquipmentList, { UserEquipmentModal } from './EquipmentList';
import SelectedEquipmentModal from './SelectedEquipmentModal';
import EquipmentSelector from './EquipmentSelector';
import EquipmentPlacer from './EquipmentPlacer';
import {
  Box,
  Flex,
  Heading,
  Button,
  Text,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertDescription,
  IconButton,
  Collapse,
  VStack,
  useDisclosure,
  HStack,
  Grid,
  GridItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Switch,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useMediaQuery,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Select,
  Tooltip,
  Circle,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Icon,
} from '@chakra-ui/react';
import { useBreakpointValue } from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon, CalendarIcon, ViewIcon, ChatIcon, HamburgerIcon, LockIcon, AddIcon, CloseIcon, QuestionIcon, CheckCircleIcon, ChevronDownIcon } from '@chakra-ui/icons';
import ChatBox from './ChatBox';
import logo from '../assets/logo.jpg';
import test from '../assets/test.png';
import GuideContent from './GuideContent';
import SelectedItemsModal from './SelectedItemsModal';
import GuideModal from './GuideModal';

const createInitialFilterState = (equipmentDefinitions) => {
  const initialFilterState = {
    showOnlyLiked: false,
    showRecommendedOnly: false,
    showOnlyUserEquipment: false,
  };

  Object.entries(equipmentDefinitions.equipment_types).forEach(([typeKey, typeDef]) => {
    if (!typeDef.fields) return;
    Object.entries(typeDef.fields).forEach(([fieldName, field]) => {
      if (field.type === 'number') {
        initialFilterState[`min_${fieldName}`] = null;
        initialFilterState[`max_${fieldName}`] = null;
      }
      if (field.type === 'select' || field.type === 'string') {
        initialFilterState[fieldName] = '';
      }
      if (field.type === 'boolean') {
        initialFilterState[fieldName] = false;
      }
    });
  });

  return initialFilterState;
};

export default function EquipmentContent({ onFixedChange, targetUserId }) {
  const { currentUser, isFunctionEnabled } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [preferences, setPreferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [equipmentDefinitions, setEquipmentDefinitions] = useState({});
  const [definitionsLoading, setDefinitionsLoading] = useState(true);

  // --- State ---
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [selectedEquipment, setSelectedEquipment] = useState('');
  const [filterState, setFilterState] = useState({});
  const [fixedMap, setFixedMap] = useState({});
  const isWide = useBreakpointValue({ base: false, md: true });
  const tabFontSize = useBreakpointValue({ base: "sm", md: "md" });
  const sidebarDisclosure = useDisclosure({ defaultIsOpen: isWide });
  const chatDisclosure = useDisclosure({ defaultIsOpen: false });
  const [isTsubo, setIsTsubo] = useState(false); // 単位切替（坪）
  const [sortState, setSortState] = useState({ key: 'popularity', direction: '' });
  const [iframeError, setIframeError] = useState(false);
  const [currentIframeSrc, setCurrentIframeSrc] = useState('');
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [showCartModal, setShowCartModal] = useState(false);
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [localUnreadItemTypes, setLocalUnreadItemTypes] = useState([]);

  // 横スクロールメニューのスクロール制御用
  const horizontalScrollRef = useRef(null);

  // 確認ダイアログ用のstate
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [confirmDialogMessage, setConfirmDialogMessage] = useState('');
  const [confirmDialogAction, setConfirmDialogAction] = useState(null);
  const confirmDialogCancelRef = React.useRef();

  // 未選択の設備種別を取得する関数
  const getUnselectedEquipmentTypes = useCallback(() => {
    if (!equipmentDefinitions.equipment_types) return [];
    
    const sortedEquipmentTypes = Object.entries(equipmentDefinitions.equipment_types)
      .sort((a, b) => (a[1].display_order || 0) - (b[1].display_order || 0))
      .filter(([key, definition]) => definition.enabled);
    
    return sortedEquipmentTypes.filter(([key]) => !fixedMap[key]);
  }, [equipmentDefinitions, fixedMap]);

  // 次の未選択設備種別を取得する関数
  const getNextUnselectedEquipmentType = useCallback(() => {
    const unselectedTypes = getUnselectedEquipmentTypes();
    return unselectedTypes.length > 0 ? unselectedTypes[0][0] : null;
  }, [getUnselectedEquipmentTypes]);

  // 全設備が選択済みかチェックする関数
  const areAllEquipmentsSelected = useCallback(() => {
    const unselectedTypes = getUnselectedEquipmentTypes();
    return unselectedTypes.length === 0;
  }, [getUnselectedEquipmentTypes]);

  // 確認ダイアログを表示する関数
  const showConfirmDialog = useCallback((message, action) => {
    setConfirmDialogMessage(message);
    setConfirmDialogAction(() => action);
    setIsConfirmDialogOpen(true);
  }, []);

  // 確認ダイアログの実行
  const handleConfirmDialogYes = useCallback(() => {
    if (confirmDialogAction) {
      confirmDialogAction();
    }
    setIsConfirmDialogOpen(false);
    setConfirmDialogAction(null);
  }, [confirmDialogAction]);

  // 確認ダイアログのキャンセル
  const handleConfirmDialogNo = useCallback(() => {
    setIsConfirmDialogOpen(false);
    setConfirmDialogAction(null);
  }, []);

  // 初期データの並列読み込み
  const loadInitialData = useCallback(async () => {
    if (!currentUser) {
      setDefinitionsLoading(false);
      setLoading(false);
      setError("ユーザー情報がありません。");
      return;
    }

    setDefinitionsLoading(true);
    setLoading(true);
    setError(null);

    try {
      // 設備定義を取得
      const definitions = await getMasterEquipmentDefinitions();

      if (!definitions || Object.keys(definitions).length === 0) {
        throw new Error('設備定義が空です');
      }

      setEquipmentDefinitions(definitions);
      
      // 設備定義を読み込んだ直後に、ユーザー設定を取得して未選択設備種別を特定
      if (definitions.equipment_types && Object.keys(definitions.equipment_types).length > 0) {
        try {
          // ユーザー設定を先に取得
          const prefsData = await getUserItemPreferences(targetUserId);
          setPreferences(prefsData);
          
          // ユーザー設定からfixedMapを計算
          const initialFixedMap = {};
          prefsData.forEach(p => {
            if (p.is_fixed === true || (p.locked_at !== null && p.locked_at !== undefined)) {
              initialFixedMap[p.item_type] = true;
            }
          });
          setFixedMap(initialFixedMap);
          
          // 未選択設備種別を特定
          const sortedEquipmentTypes = Object.entries(definitions.equipment_types)
            .sort((a, b) => (a[1].display_order || 0) - (b[1].display_order || 0))
            .filter(([key, definition]) => definition.enabled);
          
          const unselectedTypes = sortedEquipmentTypes.filter(([key]) => !initialFixedMap[key]);
          const nextUnselectedType = unselectedTypes.length > 0 ? unselectedTypes[0][0] : sortedEquipmentTypes[0][0];
          
          // 未選択設備種別（または最初の設備種別）を選択
          setSelectedEquipment(nextUnselectedType);
          
          // 選択された設備種別の人気順設定を確認してソート状態を設定
          const selectedEquipmentConfig = definitions.equipment_types[nextUnselectedType];
          if (selectedEquipmentConfig.enable_popularity !== false) {
            setSortState({ key: 'popularity', direction: '' });
          } else {
            setSortState({ key: '', direction: '' });
          }
          
          // 未読データを取得
          if (isFunctionEnabled('chat')) {
            try {
              if (targetUserId) {
                const unreadData = await getUnreadItemTypesByUserId(targetUserId);
                setLocalUnreadItemTypes(unreadData.item_types);
              } else {
                const unreadData = await getUnreadItemTypes();
                setLocalUnreadItemTypes(unreadData.item_types);
              }
            } catch (error) {
              console.error('未読データの取得に失敗:', error);
              setLocalUnreadItemTypes([]);
            }
          }
          
        } catch (err) {
          console.error("ユーザー設定の取得に失敗しました:", err);
          setPreferences([]);
          setFixedMap({});
          
          // エラー時は最初の設備種別を選択
          const sortedEquipmentTypes = Object.entries(definitions.equipment_types)
            .sort((a, b) => (a[1].display_order || 0) - (b[1].display_order || 0));
          const firstEquipmentKey = sortedEquipmentTypes[0][0];
          setSelectedEquipment(firstEquipmentKey);
          
          const firstEquipmentConfig = sortedEquipmentTypes[0][1];
          if (firstEquipmentConfig.enable_popularity !== false) {
            setSortState({ key: 'popularity', direction: '' });
          } else {
            setSortState({ key: '', direction: '' });
          }
        }
      }
      
      setFilterState(createInitialFilterState(definitions));
      
    } catch (err) {
      console.error("初期データの取得に失敗しました:", err);
      setError(err.message);
      setEquipmentDefinitions({});
    } finally {
      setDefinitionsLoading(false);
      // loading状態は loadItemsAndPreferences で管理するため、ここでは設定しない
    }
  }, [currentUser, targetUserId]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // ユーザー登録設備の取得
  const [userEquipments, setUserEquipments] = useState([]);
  const [loadingUserEquipments, setLoadingUserEquipments] = useState(false);
  const userModalDisclosure = useDisclosure();

  // 設備アイテムの読み込み（ユーザー設定は既にloadInitialDataで取得済み）
  const loadItemsAndPreferences = useCallback(async () => {
    if (!currentUser || definitionsLoading || !equipmentDefinitions.equipment_types || !equipmentDefinitions.equipment_types[selectedEquipment]) {
      if (!definitionsLoading && equipmentDefinitions.equipment_types && !equipmentDefinitions.equipment_types[selectedEquipment]) {
        console.warn(`選択された設備 (${selectedEquipment}) の定義が equipmentDefinitions.equipment_types に見つかりません。`);
      }
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // マスタ設備を取得
      const itemsData = await getMasterEquipment(selectedEquipment);
      setItems(itemsData);
        
    } catch (err) {
      console.error("アイテムデータの取得に失敗しました:", err);
      setError(err.message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [currentUser, selectedEquipment, equipmentDefinitions, definitionsLoading]);

  // selectedEquipment が変わったらアイテムと設定を読み込む
  useEffect(() => {
    if (!definitionsLoading && selectedEquipment) {
      loadItemsAndPreferences();
    }
  }, [loadItemsAndPreferences, definitionsLoading, selectedEquipment]);

  // 設備種別が変わった時に未読データを再取得
  useEffect(() => {
    const fetchUnreadData = async () => {
      if (!currentUser || !isFunctionEnabled('chat') || !selectedEquipment) {
        return;
      }

      try {
        if (targetUserId) {
          const unreadData = await getUnreadItemTypesByUserId(targetUserId);
          setLocalUnreadItemTypes(unreadData.item_types);
        } else {
          const unreadData = await getUnreadItemTypes();
          setLocalUnreadItemTypes(unreadData.item_types);
        }
      } catch (error) {
        console.error('未読データの取得に失敗:', error);
        setLocalUnreadItemTypes([]);
      }
    };

    fetchUnreadData();
  }, [currentUser, targetUserId, isFunctionEnabled, selectedEquipment]);

  // ユーザー設備の取得
  const fetchUserEquipments = useCallback(async () => {
    if (!currentUser || !selectedEquipment) return;
    setLoadingUserEquipments(true);
    try {
      const data = await getUserEquipment(selectedEquipment, targetUserId);
      setUserEquipments(data);
    } catch (err) {
      console.error("ユーザー登録設備の取得に失敗しました:", err);
      setError(err.message);
    } finally {
      setLoadingUserEquipments(false);
    }
  }, [currentUser, selectedEquipment, targetUserId]);

  useEffect(() => {
    if (selectedEquipment) {
      fetchUserEquipments();
    }
  }, [fetchUserEquipments]);

  // iframeの読み込みタイムアウトを管理
  const [loadingTimeoutId, setLoadingTimeoutId] = useState(null);

  // 利用可能なガイドページのリスト
  const AVAILABLE_GUIDES = ['Kitchen', 'Bath', 'Basin', 'Floorplan', 'Toilet'];

  // iframeの遅延読み込み（タブがアクティブになった時に実行）
  const loadIframe = useCallback(() => {
    if (iframeLoaded) return; // 既に読み込まれている場合は何もしない
    
    // 選択された設備のガイドページ名を生成
    const guideName = selectedEquipment.charAt(0).toUpperCase() + selectedEquipment.slice(1);
    
    // ガイドページが利用可能かチェック
    if (AVAILABLE_GUIDES.includes(guideName)) {
      // iframeの初期設定
      const targetUrl = `https://www.himawarin-lab.com/guide/${guideName}`;
      setCurrentIframeSrc(targetUrl);
      setIframeError(false);
      setIframeLoaded(true);
      
      // 10秒後にエラー表示（読み込みが遅い、または無効なURLの場合）
      const timeoutId = setTimeout(() => {
        setIframeError(true);
        setCurrentIframeSrc('');
      }, 10000);
      
      setLoadingTimeoutId(timeoutId);
    } else {
      // ガイドページが存在しない場合は即座にエラー表示
      setIframeError(true);
      setCurrentIframeSrc('');
      setIframeLoaded(true);
    }
  }, [selectedEquipment, iframeLoaded]);

  // 現在選択中の設備の設定を取得
  const currentEquipmentConfig = useMemo(() => {
    if (definitionsLoading || Object.keys(equipmentDefinitions).length === 0) {
      let label = '設備定義を読込中...';

      if (!definitionsLoading && Object.keys(equipmentDefinitions).length === 0) {
        label = error ? '設備定義読込エラー' : '設備定義なし';
      }
      return {
        key: selectedEquipment,
        label: label,
        enabled: false,
        enable_popularity: true,
        enable_guide: true,
        icon: '❓',
        fields: []
      };
    }

    const definition = equipmentDefinitions['equipment_types'][selectedEquipment];
    if (!definition) {
      console.warn(`設備定義が見つかりません: ${selectedEquipment}`);
      return {
        key: selectedEquipment,
        label: '定義が見つかりません',
        enabled: false,
        enable_popularity: true,
        enable_guide: true,
        icon: '❓',
        fields: []
      };
    }
    return {
      key: selectedEquipment,
      label: definition.name,
      enabled: definition.enabled,
      enable_popularity: definition.enable_popularity,
      enable_guide: definition.enable_guide,
      icon: definition.icon,
      hover_text: definition.hover_text,
      fields: Object.entries(definition.fields || {}).map(([name, field]) => ({
        name,
        ...field
      }))
    };
  }, [selectedEquipment, equipmentDefinitions, definitionsLoading, error]);

  // タブが変わった時にiframeを読み込む
  useEffect(() => {
    // ガイドタブのインデックスを計算（ガイドが有効な場合は2、無効な場合は存在しない）
    const guideTabIndex = currentEquipmentConfig?.enable_guide !== false ? 2 : -1;
    if (activeTab === guideTabIndex && !iframeLoaded && guideTabIndex !== -1) {
      loadIframe();
    }
  }, [activeTab, iframeLoaded, loadIframe, currentEquipmentConfig]);

  // 選択された設備が変わったら、フィルター状態を初期値に戻す
  useEffect(() => {
    if (Object.keys(equipmentDefinitions).length > 0) {
      setFilterState(createInitialFilterState(equipmentDefinitions));
    }
    
    // 前のタイムアウトをクリア
    if (loadingTimeoutId) {
      clearTimeout(loadingTimeoutId);
    }
    
    // 設備が変わったらiframeの状態をリセット
    setIframeError(false);
    setCurrentIframeSrc('');
    setIframeLoaded(false);
  }, [selectedEquipment, equipmentDefinitions]);

  // 設備種別変更時にソート状態をリセットし、人気順が有効な場合は人気順でソート
  useEffect(() => {
    if (currentEquipmentConfig && selectedEquipment) {
      // 人気順が有効な場合は人気順でソート、無効な場合はソートをクリア
      if (currentEquipmentConfig.enable_popularity !== false) {
        setSortState({ key: 'popularity', direction: '' });
      } else {
        setSortState({ key: '', direction: '' });
      }
    }
  }, [selectedEquipment, currentEquipmentConfig?.enable_popularity]);

  // 横スクロールメニューで選択された設備種別を中央にスクロール
  useEffect(() => {
    if (horizontalScrollRef.current && selectedEquipment) {
      const scrollContainer = horizontalScrollRef.current;
      const selectedElement = scrollContainer.querySelector(`[data-equipment-key="${selectedEquipment}"]`);
      
      if (selectedElement) {
        const containerWidth = scrollContainer.offsetWidth;
        const elementLeft = selectedElement.offsetLeft;
        const elementWidth = selectedElement.offsetWidth;
        
        // 選択された要素を中央に配置
        const scrollLeft = elementLeft - (containerWidth / 2) + (elementWidth / 2);
        
        scrollContainer.scrollTo({
          left: Math.max(0, scrollLeft),
          behavior: 'smooth'
        });
      }
    }
  }, [selectedEquipment]);

  // 人気順が無効な場合、人気順ソートをリセット
  useEffect(() => {
    if (currentEquipmentConfig?.enable_popularity === false && sortState.key === 'popularity') {
      setSortState({ key: '', direction: '' });
    }
  }, [currentEquipmentConfig?.enable_popularity, sortState.key]);

  // コンポーネントアンマウント時にタイムアウトをクリア
  useEffect(() => {
    return () => {
      if (loadingTimeoutId) {
        clearTimeout(loadingTimeoutId);
      }
    };
  }, [loadingTimeoutId]);





  // --- ここで isFloorplanSelected を定義 ---
  const isFloorplanSelected = useMemo(() => {
    return selectedEquipment === 'floorplan';
  }, [selectedEquipment]);

  // データ結合
  const combinedData = useMemo(() => {
    // マスタ設備とユーザー設定の結合
    const masterItems = items.map(item => {
      const preference = preferences.find(p => 
        p.item_type === selectedEquipment && p.item_id === item.id
      );
      return {
        ...item,
        item_type: selectedEquipment,
        preferenceId: preference?.id,
        is_liked: preference?.is_liked ?? false,
        is_fixed: preference?.is_fixed ?? false,
        locked_at: preference?.locked_at ?? null,
        signature_url: preference?.signature_url ?? null,
        quantity: preference?.quantity ?? 1,
        memo: preference?.memo ?? null,
        material_files: preference?.material_files ?? [],
        is_user_equipment: false
      };
    });

    // ユーザー登録設備とユーザー設定の結合
    const userItems = userEquipments.map(item => {
      const preference = preferences.find(p => 
        p.item_type === selectedEquipment && p.item_id === item.id
      );
      return {
        ...item,
        id: `${item.id}`,
        item_type: selectedEquipment,
        preferenceId: preference?.id,
        is_liked: preference?.is_liked ?? false,
        is_fixed: preference?.is_fixed ?? false,
        locked_at: preference?.locked_at ?? null,
        signature_url: preference?.signature_url ?? null,
        quantity: preference?.quantity ?? 1,
        memo: preference?.memo ?? null,
        material_files: preference?.material_files ?? [],
        is_user_equipment: true
      };
    });

    // マスタ設備に存在しないが、preferencesに存在するアイテムを追加
    const orphanedPreferences = preferences.filter(p => 
      p.item_type === selectedEquipment && 
      !items.some(item => item.id === p.item_id) &&
      !userEquipments.some(item => item.id === p.item_id)
    );

    const orphanedItems = orphanedPreferences.map(pref => ({
      id: pref.item_id,
      name: `削除されたアイテム (ID: ${pref.item_id})`,
      item_type: selectedEquipment,
      preferenceId: pref.id,
      is_liked: pref.is_liked ?? false,
      is_fixed: pref.is_fixed ?? false,
      locked_at: pref.locked_at ?? null,
      signature_url: pref.signature_url ?? null,
      quantity: pref.quantity ?? 1,
      memo: pref.memo ?? null,
      material_files: pref.material_files ?? [],
      is_user_equipment: false,
      is_deleted: true
    }));

    const result = [...masterItems, ...userItems, ...orphanedItems];

    return result;
  }, [items, userEquipments, preferences, selectedEquipment]);


  // フィックスされたアイテム（複数対応）
  const fixedItems = useMemo(() => {
    const filtered = combinedData.filter(item => 
      item.item_type === selectedEquipment && 
      (item.is_fixed || item.locked_at) && 
      !item.is_deleted
    );
    
    return filtered;
  }, [combinedData, selectedEquipment]);

  // 設備一覧用のデータ（削除されたアイテムを除外）
  const displayItems = useMemo(() => {
    return combinedData.filter(item => !item.is_deleted);
  }, [combinedData]);

  // 確定アイテムの合計金額（数量×価格）
  const fixedTotalPrice = useMemo(() => {
    return fixedItems.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0);
  }, [fixedItems]);

  // 仮確定状態を親に通知
  useEffect(() => {
    if (typeof onFixedChange === 'function') {
      onFixedChange(fixedItems.length > 0);
    }
  }, [fixedItems, onFixedChange]);

  // 仮確定情報をfixedMapに反映（preferencesベースで判定）
  useEffect(() => {
    const map = {};
    
    preferences.forEach(p => {
      // 暫定アイテムまたはロック済みアイテムが存在する場合のみ選択中とする
      if (p.is_fixed === true || (p.locked_at !== null && p.locked_at !== undefined)) {
        map[p.item_type] = true;
      }
    });
    
    setFixedMap(map);
  }, [preferences]);

  // 初期自動選択はloadInitialDataで処理済みのため削除

  // フィルター適用状況を判定
  const isFilterApplied = useMemo(() => {
    if (!currentEquipmentConfig) return false;
    
    // 共通フィルターの確認
    if (filterState.showRecommendedOnly || filterState.showOnlyLiked || filterState.showOnlyUserEquipment) {
      return true;
    }
    
    // 設備特有のフィルターの確認
    return currentEquipmentConfig.fields?.some(field => {
      if (field.type === 'number') {
        const minKey = `min_${field.name}`;
        const maxKey = `max_${field.name}`;
        return !isNaN(parseFloat(filterState[minKey])) || !isNaN(parseFloat(filterState[maxKey]));
      }
      if (field.type === 'select' || field.type === 'string') {
        const value = filterState[field.name];
        return value && (Array.isArray(value) ? value.length > 0 : value.trim() !== '');
      }
      if (field.type === 'boolean') {
        return filterState[field.name] === true;
      }
      return false;
    }) || false;
  }, [filterState, currentEquipmentConfig]);

  // フィルター/ソート適用後の表示データ
  const filteredData = useMemo(() => {
    if (!currentEquipmentConfig) return [];
    let result = [...displayItems];

    // 共通フィルター
    if (filterState.showRecommendedOnly) {
      result = result.filter(item => item.recommended === true);
    }
    if (filterState.showOnlyLiked) {
      result = result.filter(item => item.is_liked);
    }
    if (filterState.showOnlyUserEquipment) {
      result = result.filter(item => item.is_user_equipment);
    }
    
    // 設備特有のフィルター
    currentEquipmentConfig.fields?.forEach(field => {
      if (field.type === 'number') {
        const minKey = `min_${field.name}`;
        const maxKey = `max_${field.name}`;
        let minValue = parseFloat(filterState[minKey]);
        let maxValue = parseFloat(filterState[maxKey]);
        
        if( isTsubo && field.unit === 'm2' ) {
          minValue = minValue * 3.305785;
          maxValue = maxValue * 3.305785;
        }
        if (!isNaN(minValue) || !isNaN(maxValue)) {
          result = result.filter(item => {
            const value = item[field.name] ?? -Infinity;
            const passes = (!isNaN(minValue) ? value >= minValue : true) && 
                          (!isNaN(maxValue) ? value <= maxValue : true);
            return passes;
          });
        }
      }
      
      if (field.type === 'select' || field.type === 'string') {
        if (filterState[field.name]) {
          if (Array.isArray(filterState[field.name])) {
            // 複数選択の場合：OR条件（配列の中にアイテムの値が含まれているかチェック）
            // 末尾のスペースを無視するため、trim()を使用して比較
            result = result.filter(item => 
              filterState[field.name].some(filterValue => 
                (item[field.name] || '').trim() === filterValue.trim()
              )
            );
          } else {
            // 単一選択の場合：末尾のスペースを無視するため、trim()を使用して比較
            result = result.filter(item => 
              (item[field.name] || '').trim() === filterState[field.name].trim()
            );
          }
        }
      }
      
      if (field.type === 'boolean') {
        if (filterState[field.name]) {
          result = result.filter(item => item[field.name] === true);
        }
      }
    });

    // ソート機能
    if (sortState.key) {
      if (sortState.key === 'popularity') {
        // 人気順：元の配列順を保持（マスタ設備→ユーザー設備の順）
        // 何もしない（resultはそのまま）
      } else if (sortState.direction) {
        // 数値フィールドでの並び替え
        result = [...result].sort((a, b) => {
          const field = sortState.key;
          if (a[field] == null && b[field] == null) return 0;
          if (a[field] == null) return 1;
          if (b[field] == null) return -1;
          if (a[field] === b[field]) return 0;
          if (sortState.direction === 'asc') {
            return a[field] - b[field];
          } else if (sortState.direction === 'desc') {
            return b[field] - a[field];
          }
          return 0;
        });
      }
    }

    // ランキング装飾を追加（人気順で初期フィルタ状態の場合のみ）
    const isInitialFilterState = !filterState.showOnlyLiked && 
                                 !filterState.showRecommendedOnly && 
                                 !filterState.showOnlyUserEquipment &&
                                 (!currentEquipmentConfig?.fields || 
                                  currentEquipmentConfig.fields.every(field => {
                                    if (field.type === 'number') {
                                      return !filterState[`min_${field.name}`] && !filterState[`max_${field.name}`];
                                    }
                                    if (field.type === 'select' || field.type === 'string') {
                                      return !filterState[field.name] || 
                                             (Array.isArray(filterState[field.name]) && filterState[field.name].length === 0);
                                    }
                                    if (field.type === 'boolean') {
                                      return !filterState[field.name];
                                    }
                                    return true;
                                  }));

    // 人気順表示が有効で、人気順ソートかつ初期フィルタ状態の場合のみランキング表示
    const isPopularityEnabled = currentEquipmentConfig?.enable_popularity !== false;
    if (sortState.key === 'popularity' && isInitialFilterState && isPopularityEnabled) {
      // ユーザー登録設備を除いたマスタ設備の上位3つにランキングを設定
      let masterItemRank = 1;
      result = result.map((item, index) => {
        if (!item.is_user_equipment && masterItemRank <= 3) {
          const rankingBadge = masterItemRank;
          masterItemRank++;
          return { ...item, rankingBadge };
        }
        return { ...item, rankingBadge: null };
      });
    } else {
      // 人気順以外またはフィルタが適用されている場合、または人気順が無効の場合はランキングバッジを削除
      result = result.map(item => ({ ...item, rankingBadge: null }));
    }

    return result;
  }, [combinedData, filterState, currentEquipmentConfig, sortState, isTsubo]);

  // 選択されたアイテム
  const selectedItem = useMemo(() => {
    if (selectedItemId === null) return null;
    return displayItems.find(item => item.id === selectedItemId) ||
           items.find(item => item.id === selectedItemId);
  }, [selectedItemId, displayItems, items]);

  // ハンドラー
  const handleItemSelect = (itemId) => { setSelectedItemId(itemId); };
  const closeModal = () => { setSelectedItemId(null); };

  // ユーザー設備登録ハンドラー
  const handleRegisterEquipment = async (formData) => {
    try {
      // 画像ファイルのアップロード（description付き）
      const imageUrls = [];
      
      // 新しい形式（imageData）を優先的にチェック
      if (formData.imageData && formData.imageData.length > 0) {
        for (const imageItem of formData.imageData) {
          const imageData = await uploadUserEquipmentImage(
            currentEquipmentConfig.key, 
            imageItem.file, 
            currentUser, 
            targetUserId || currentUser.uid, 
            imageItem.description
          );
          imageUrls.push(imageData); // オブジェクト形式で保存（{url, description}）
        }
      }
      // 従来の形式（images）のフォールバック処理
      else if (formData.images && formData.images.length > 0) {
        for (const imageFile of formData.images) {
          const imageData = await uploadUserEquipmentImage(
            currentEquipmentConfig.key, 
            imageFile, 
            currentUser, 
            targetUserId || currentUser.uid, 
            ''
          );
          imageUrls.push(imageData); // オブジェクト形式で保存（{url, description}）
        }
      }

      // 設備データの登録
      const equipmentData = {
        ...formData,
        images: imageUrls,
        user_id: targetUserId || currentUser.uid,
        item_type: currentEquipmentConfig.key
      };
      
      // imageDataプロパティは不要なので削除
      delete equipmentData.imageData;

      await createUserEquipment(currentEquipmentConfig.key, equipmentData);

      toast({
        title: '登録完了',
        description: '設備を登録しました',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // 設備一覧を再読み込み
      await loadItemsAndPreferences();
      await fetchUserEquipments();
      userModalDisclosure.onClose();
    } catch (error) {
      toast({
        title: 'エラー',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // お気に入り/フィックス状態の更新処理
  const handlePreferenceChange = useCallback(async (itemId, itemType, newValueObj) => {
    setError('');
    const currentItem = displayItems.find(item => item.id === itemId) || combinedData.find(item => item.id === itemId);
    if (!currentItem) { console.error("Target item not found:", itemId); return; }

    // 現在のpreferenceを取得
    const currentPreference = preferences.find(
      p => p.item_type === itemType && p.item_id === itemId
    );
    const currentPreferenceId = currentPreference?.id;
    const originalPreferences = [...preferences];

    // --- Optimistic Update ---
    setPreferences(prevPreferences => {
      let preferenceExists = false;
      const newPreferences = prevPreferences.map(pref => {
        let updatedPref = { ...pref };
        if (pref.item_type === itemType && pref.item_id === itemId) {
          updatedPref = { ...updatedPref, ...newValueObj };
          preferenceExists = true;
        }
        return updatedPref;
      });
      if (!preferenceExists) {
        const newPref = {
          item_type: itemType,
          item_id: itemId,
          user_id: targetUserId || currentUser.uid,
          is_liked: newValueObj.is_liked ?? false,
          is_fixed: newValueObj.is_fixed ?? false,
          quantity: newValueObj.quantity ?? currentItem.quantity ?? 1,
          memo: newValueObj.memo ?? null,
          material_files: newValueObj.material_files ?? [],
          ...newValueObj
        };
        newPreferences.push(newPref);
      }
      return newPreferences;
    });

    try {
      let updatedPref;
      if (currentPreferenceId) {
        updatedPref = await updateUserItemPreference(
          currentPreferenceId,
          { ...newValueObj, quantity: newValueObj.quantity ?? currentItem.quantity ?? 1 },
          currentUser
        );
      } else {
        const requestData = {
          item_type: itemType,
          item_id: itemId,
          is_liked: newValueObj.is_liked ?? currentItem.is_liked,
          is_fixed: newValueObj.is_fixed ?? currentItem.is_fixed,
          quantity: newValueObj.quantity ?? currentItem.quantity ?? 1,
          memo: newValueObj.memo ?? null,
          material_files: newValueObj.material_files ?? [],
          user_id: targetUserId || currentUser.uid,
          ...newValueObj
        };
        updatedPref = await createUserItemPreference(requestData, currentUser, targetUserId);
      }
      setPreferences(prev => [
        ...prev.filter(p => !(p.item_type === itemType && p.item_id === itemId)),
        updatedPref
      ]);

      // 暫定ボタンが押された場合の処理（確認ダイアログのみ表示、自動移動はしない）
      if (newValueObj.hasOwnProperty('is_fixed') && newValueObj.is_fixed === true) {
        // 少し遅延してから確認ダイアログを表示（状態更新が完了してから）
        setTimeout(() => {
          // 現在のpreferencesから暫定・ロック済みアイテムをチェック
          const currentFixedPrefs = preferences.filter(p => p.is_fixed === true || p.locked_at);
          const updatedFixedPrefs = [...currentFixedPrefs, { item_type: itemType, is_fixed: true }];
          
          // 更新後のfixedMapを計算
          const updatedFixedMap = {};
          updatedFixedPrefs.forEach(p => {
            if (p.is_fixed === true || (p.locked_at !== null && p.locked_at !== undefined)) {
              updatedFixedMap[p.item_type] = true;
            }
          });
          
          // 更新されたfixedMapを使って次の未選択設備種別を取得
          const sortedEquipmentTypes = Object.entries(equipmentDefinitions.equipment_types || {})
            .sort((a, b) => (a[1].display_order || 0) - (b[1].display_order || 0))
            .filter(([key, definition]) => definition.enabled);
          
          const unselectedTypes = sortedEquipmentTypes.filter(([key]) => !updatedFixedMap[key]);
          const nextUnselectedType = unselectedTypes.length > 0 ? unselectedTypes[0][0] : null;
          
          if (nextUnselectedType) {
            // 次の未選択設備種別がある場合
            const nextEquipmentName = equipmentDefinitions.equipment_types[nextUnselectedType]?.name || nextUnselectedType;
            showConfirmDialog(
              `${nextEquipmentName}の選択に移りますか？`,
              () => setSelectedEquipment(nextUnselectedType)
            );
          } else if (unselectedTypes.length === 0) {
            // 全設備が選択完了した場合
            showConfirmDialog(
              '概算見積もりページに移りますか？',
              () => {
                const estimateUrl = targetUserId 
                  ? `/admin/user/${targetUserId}/equipment/confirmed-summary` 
                  : '/equipment/confirmed-summary';
                navigate(estimateUrl);
              }
            );
          }
        }, 100);
      }
    } catch (err) {
      console.error("[handlePreferenceChange] Error during preference update, reverting state:", err);
      setPreferences(originalPreferences);
      let errorMessage = '設定の更新に失敗しました';
      if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    }
  }, [combinedData, preferences, currentUser, targetUserId, getNextUnselectedEquipmentType, areAllEquipmentsSelected, equipmentDefinitions, showConfirmDialog, navigate]);

  // 画面幅が変わったら開閉を自動で切り替える
  useEffect(() => {
    if (isWide && !sidebarDisclosure.isOpen) {
      sidebarDisclosure.onOpen();
    } else if (!isWide && sidebarDisclosure.isOpen) {
      sidebarDisclosure.onClose();
    }
    if (isWide && !chatDisclosure.isOpen) {
      chatDisclosure.onOpen();
    } else if (!isWide && chatDisclosure.isOpen) {
      chatDisclosure.onClose();
    }
    // eslint-disable-next-line
  }, [isWide]);



  // 設備定義の読み込みが完了していない場合は基本的なローディング表示
  if (definitionsLoading) {
    return (
      <Flex align="center" justify="center" minH="60vh">
        <Text fontSize="xl" color="gray.500">設備定義を読み込み中...</Text>
      </Flex>
    );
  }

  // --- Chakra UI用の色 ---
  const mainBg = useColorModeValue('white', 'gray.900');

  return (
    <Flex minH="100vh" w="100vw" bg={mainBg}>
      {/* サイドバー - モバイルでは非表示 */}
      {isWide && (
        <Box
          w={sidebarDisclosure.isOpen ? '220px' : '40px'}
          minW={sidebarDisclosure.isOpen ? '220px' : '40px'}
          bg="gray.50"
          borderRightWidth="1px"
          p={2}
          position="relative"
        > 
          <IconButton
            icon={sidebarDisclosure.isOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            onClick={sidebarDisclosure.onToggle}
            aria-label="ナビを開閉"
            mb={2}
            size="sm"
            colorScheme="gray"
            borderWidth={1}
            borderColor="black"
          />
          <EquipmentSelector
            onSelect={setSelectedEquipment}
            selected={selectedEquipment}
            fixedMap={fixedMap}
            equipmentList={equipmentDefinitions}
            isOpen={sidebarDisclosure.isOpen}
            targetUserId={targetUserId}
          />
        </Box>
      )}
      {/* メインコンテンツ */}
      <Box flex={1} px={{ base: 1, md: 4 }} minW={0}>
        <Flex gap={4} position="relative">
          {/* 左側のメインコンテンツ */}
          <Box 
            flex={1} 
            minW="0" 
            transition="width 0.3s ease-in-out"
            w="100%"
          >
            {error && (
              <Alert status="error" mb={4} borderRadius="md">
                <AlertIcon />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* 設備種別表示 - タップ可能な見出し */}
            <Box mb={4} px={4} py={3} bg="gray.50" borderRadius="md" borderWidth="1px" borderColor="gray.200">
              <Flex justify="space-between" align="center">
                <Menu>
                  <MenuButton
                    as={Button}
                    variant="ghost"
                    size="lg"
                    px={0}
                    py={2}
                    h="auto"
                    minH="auto"
                    fontWeight="bold"
                    color="gray.800"
                    _hover={{ bg: 'gray.100', transform: 'translateY(-1px)', boxShadow: 'sm', borderColor: 'gray.300' }}
                    _active={{ bg: 'gray.200' }}
                    rightIcon={<ChevronDownIcon />}
                    textAlign="left"
                    justifyContent="flex-start"
                    cursor="pointer"
                    transition="all 0.2s"
                    borderWidth="1px"
                    borderColor="transparent"
                  >
                    <Flex direction="column" align="flex-start">
                      <Text fontSize="lg" fontWeight="bold" color="gray.800">
                        {currentEquipmentConfig?.label || '設備種別'}
                      </Text>
                      {currentEquipmentConfig?.label && currentEquipmentConfig?.hover_text && (
                        <Text fontSize="md" fontWeight="normal" color="gray.600">
                          （{currentEquipmentConfig.hover_text}）
                        </Text>
                      )}
                    </Flex>
                  </MenuButton>
                  <MenuList>
                    {Object.entries(equipmentDefinitions.equipment_types || {}).map(([key, eq]) => {
                      const isSelected = selectedEquipment === key;
                      const hasSelectedItems = fixedMap[key] === true;
                      const hasUnreadChat = localUnreadItemTypes.includes(key);
                      return (
                        <MenuItem
                          key={key}
                          onClick={() => setSelectedEquipment(key)}
                          bg={isSelected ? 'yellow.50' : undefined}
                          color={isSelected ? 'yellow.700' : undefined}
                          fontWeight={isSelected ? 'bold' : 'normal'}
                          borderLeft={isSelected ? '4px solid yellow.400' : undefined}
                          pl={isSelected ? 3 : 4}
                          position="relative"
                        >
                          <HStack spacing={2} w="100%" justify="space-between">
                            <HStack spacing={2}>
                              {hasUnreadChat && (
                                <Circle size="8px" bg="yellow.700" />
                              )}
                              <VStack align="flex-start" spacing={0}>
                                <Text>{eq.name}</Text>
                                {eq.hover_text && (
                                  <Text fontSize="xs" color="gray.600">
                                    （{eq.hover_text}）
                                  </Text>
                                )}
                              </VStack>
                            </HStack>
                            {hasSelectedItems && (
                              <Box
                                position="absolute"
                                top="0"
                                left="0"
                                right="0"
                                bottom="0"
                                bg="rgba(0, 0, 0, 0.03)"
                                borderRadius="md"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                pointerEvents="none"
                                zIndex={1}
                              >
                                <Icon 
                                  as={CheckCircleIcon} 
                                  boxSize="16px" 
                                  color="gray.400"
                                  opacity={0.5}
                                />
                              </Box>
                            )}
                          </HStack>
                        </MenuItem>
                      );
                    })}
                  </MenuList>
                </Menu>
                <Flex gap={4} display={{ base: "flex", md: "none" }}>
                  <Box position="relative" textAlign="center">
                    <Tooltip label="見積アイテム">
                      <IconButton
                        icon={<CheckCircleIcon />}
                        onClick={() => setShowCartModal(true)}
                        colorScheme="gray"
                        variant="ghost"
                        size="sm"
                        mb={1}
                      />
                    </Tooltip>
                    {fixedItems.length > 0 && (
                      <Circle
                        size="20px"
                        bg="yellow.400"
                        color="white"
                        fontSize="xs"
                        fontWeight="bold"
                        position="absolute"
                        top="-5px"
                        right="-5px"
                      >
                        {fixedItems.length}
                      </Circle>
                    )}
                    <Text fontSize="xs" color="gray.600" fontWeight="medium">
                      選択中
                    </Text>
                  </Box>
                  {currentEquipmentConfig?.enable_guide !== false && (
                    <Box textAlign="center">
                      <Tooltip label="選び方ガイド">
                        <IconButton
                          icon={<QuestionIcon />}
                          onClick={() => setShowGuideModal(true)}
                          colorScheme="gray"
                          variant="ghost"
                          size="sm"
                          mb={1}
                        />
                      </Tooltip>
                      <Text fontSize="xs" color="gray.600" fontWeight="medium">
                        ガイド
                      </Text>
                    </Box>
                  )}
                </Flex>
              </Flex>
            </Box>

            {/* モバイル専用の設備種別ナビゲーション */}
            <Box 
              display={{ base: "block", md: "none" }} 
              mb={4} 
              px={2}
              overflowX="auto"
              ref={horizontalScrollRef}
              css={{
                '&::-webkit-scrollbar': {
                  height: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'gray.300',
                  borderRadius: '2px',
                },
              }}
            >
              <Flex gap={2} minW="max-content" pb={2}>
                {Object.entries(equipmentDefinitions.equipment_types || {})
                  .sort((a, b) => (a[1].display_order || 0) - (b[1].display_order || 0))
                  .filter(([key, definition]) => definition.enabled)
                  .map(([key, eq]) => {
                    const isSelected = selectedEquipment === key;
                    const hasSelectedItems = fixedMap[key] === true;
                    const hasUnreadChat = localUnreadItemTypes.includes(key);
                    
                    return (
                      <Box
                        key={key}
                        data-equipment-key={key}
                        onClick={() => setSelectedEquipment(key)}
                        cursor="pointer"
                        px={4}
                        py={3}
                        bg="white"
                        color="gray.800"
                        borderRadius="lg"
                        borderWidth="2px"
                        borderColor={isSelected ? 'yellow.400' : hasSelectedItems ? 'gray.400' : 'gray.200'}
                        position="relative"
                        transition="all 0.2s"
                        _hover={{
                          transform: 'translateY(-1px)',
                          boxShadow: 'md',
                          borderColor: isSelected ? 'yellow.500' : 'gray.400'
                        }}
                        minW="max-content"
                        whiteSpace="nowrap"
                      >
                        <Flex align="center" gap={2}>
                          {hasUnreadChat && (
                            <Circle size="8px" bg="yellow.700" />
                          )}
                          <VStack align="flex-start" spacing={0}>
                            <Text 
                              fontSize="sm" 
                              fontWeight="bold"
                              color="gray.800"
                            >
                              {eq.name}
                            </Text>
                            {eq.hover_text && (
                              <Text 
                                fontSize="xs" 
                                color="gray.600"
                              >
                                {eq.hover_text}
                              </Text>
                            )}
                          </VStack>
                          {hasSelectedItems && (
                            <Icon 
                              as={CheckCircleIcon} 
                              boxSize="16px" 
                              color={isSelected ? 'yellow.400' : 'gray.400'}
                            />
                          )}
                        </Flex>
                      </Box>
                    );
                  })}
                
                {/* 概算見積もりボタン */}
                <Button
                  as={RouterLink}
                  to={targetUserId ? `/admin/user/${targetUserId}/equipment/confirmed-summary` : '/equipment/confirmed-summary'}
                  bg="gray.700"
                  color="white"
                  _hover={{ bg: "gray.800" }}
                  _active={{ bg: "gray.900" }}
                  px={4}
                  py={3}
                  borderRadius="lg"
                  fontSize="sm"
                  fontWeight="bold"
                  minW="max-content"
                  whiteSpace="nowrap"
                  h="auto"
                  minH="auto"
                >
                  概算見積
                </Button>
              </Flex>
            </Box>

            {/* 単位切替スイッチ（floorplanのみ） */}
            {selectedEquipment === 'floorplan' && (
              <Flex align="center" mb={4} gap={4} px={4} bg="gray.50" p={3} borderRadius="md" borderWidth="1px" borderColor="gray.200">
                <Text fontSize="sm" fontWeight="medium">面積単位：</Text>
                <Text fontSize="sm">m²</Text>
                <Switch isChecked={isTsubo} onChange={e => setIsTsubo(e.target.checked)} colorScheme="teal" />
                <Text fontSize="sm">坪</Text>
              </Flex>
            )}

            {/* 検索コンテンツ */}
                  <Flex direction="column" gap={4} w="100%">
                    <Box w="100%">
                      <EquipmentFilter
                        config={currentEquipmentConfig}
                        filterState={filterState}
                        onFilterChange={setFilterState}
                        items={loading ? [] : filteredData}
                        isTsubo={isTsubo}
                        sortState={sortState}
                        onSortChange={setSortState}
                      />
                    </Box>
                    
                    {/* 設備件数表示 */}
                    {!loading && (
                      <Box w="100%" px={2}>
                        <Text fontSize="sm" color="gray.600">
                          {isFilterApplied ? (
                            <>
                              {filteredData.length}件見つかりました
                              {displayItems.length !== filteredData.length && (
                                <Text as="span" color="gray.500" ml={2}>
                                  （全{displayItems.length}件中）
                                </Text>
                              )}
                            </>
                          ) : (
                            `全${filteredData.length}件`
                          )}
                        </Text>
                      </Box>
                    )}
                    
                    {/* 自分で設備を登録ボタン */}
                    <Box w="100%" mb={4}>
                      <Button
                        leftIcon={<AddIcon />}
                        colorScheme="gray"
                        variant="outline"
                        size="lg"
                        w="100%"
                        onClick={userModalDisclosure.onOpen}
                        _hover={{ bg: useColorModeValue('gray.100', 'gray.900') }}
                      >
                        自分で{currentEquipmentConfig.key === 'floorplan' ? '間取り' : '設備'}を登録
                      </Button>
                    </Box>
                    
                    <Box w="100%">
                      {loading ? (
                        <Flex align="center" justify="center" minH="200px">
                          <Text fontSize="lg" color="gray.500">設備一覧を読み込み中...</Text>
                        </Flex>
                      ) : (
                        <EquipmentList
                          items={filteredData}
                          config={currentEquipmentConfig}
                          onPreferenceChange={handlePreferenceChange}
                          onItemSelect={handleItemSelect}
                          onReloadMaterialFiles={loadItemsAndPreferences}
                          onReloadPreferences={loadItemsAndPreferences}
                          user_item_preferences={preferences}
                          onReloadUserEquipments={fetchUserEquipments}
                          targetUserId={targetUserId || currentUser.uid}
                          isTsubo={isTsubo}
                        />
                      )}
                    </Box>
                  </Flex>
          </Box>
          {/* チャット機能が有効な場合のみ表示 */}
          {isFunctionEnabled('chat') && (
            <>
              {/* デスクトップ用縦書きチャットボタン */}
              <Box
                position="fixed"
                right="0"
                top="50%"
                transform="translateY(-50%)"
                zIndex={20}
                display={{ base: "none", md: chatDisclosure.isOpen ? "none" : "block" }}
              >
                <Button
                  onClick={chatDisclosure.onToggle}
                  bg="rgba(0, 0, 0, 0.7)"
                  color="white"
                  _hover={{ bg: "rgba(0, 0, 0, 0.8)" }}
                  _active={{ bg: "rgba(0, 0, 0, 0.9)" }}
                  borderRadius="md"
                  px={3}
                  py={8}
                  fontSize="sm"
                  fontWeight="medium"
                  boxShadow="lg"
                  backdropFilter="blur(10px)"
                  minH="120px"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gap={2}
                  sx={{
                    writingMode: 'vertical-rl',
                    textOrientation: 'mixed',
                    '& > *': {
                      writingMode: 'vertical-rl',
                      textOrientation: 'mixed'
                    }
                  }}
                >
                  <ChatIcon boxSize="16px" />
                  <Text
                    fontSize="sm"
                    fontWeight="medium"
                    sx={{
                      writingMode: 'vertical-rl',
                      textOrientation: 'mixed'
                    }}
                  >
                    チャットで相談
                  </Text>
                </Button>
              </Box>
              
              {/* デスクトップ用下部固定選択中アイテムボタン */}
              <Box
                position="fixed"
                bottom="20px"
                left={sidebarDisclosure.isOpen ? "240px" : "60px"}
                zIndex={20}
                display={{ base: "none", md: showCartModal ? "none" : "block" }}
              >
                <Button
                  onClick={() => setShowCartModal(true)}
                  bg="rgba(0, 0, 0, 0.7)"
                  color="white"
                  _hover={{ bg: "rgba(0, 0, 0, 0.8)" }}
                  _active={{ bg: "rgba(0, 0, 0, 0.9)" }}
                  borderRadius="full"
                  px={4}
                  py={1}
                  h="32px"
                  fontSize="sm"
                  fontWeight="medium"
                  boxShadow="lg"
                  backdropFilter="blur(10px)"
                >
                  <HStack spacing={1} align="center">
                    <Box position="relative">
                      <CheckCircleIcon boxSize="14px" />
                      {fixedItems.length > 0 && (
                        <Circle
                          size="14px"
                          bg="yellow.400"
                          color="white"
                          fontSize="xs"
                          fontWeight="bold"
                          position="absolute"
                          top="-6px"
                          right="-6px"
                        >
                          {fixedItems.length}
                        </Circle>
                      )}
                    </Box>
                    <Text>選択中アイテム</Text>
                  </HStack>
                </Button>
              </Box>
              
              {/* デスクトップ用下部固定ガイドボタン */}
              {currentEquipmentConfig?.enable_guide !== false && (
                <Box
                  position="fixed"
                  bottom="20px"
                  left={sidebarDisclosure.isOpen ? "240px" : "60px"}
                  zIndex={20}
                  display={{ base: "none", md: "block" }}
                  transform="translateY(-60px)"
                >
                  <Button
                    onClick={() => setShowGuideModal(true)}
                    bg="rgba(0, 0, 0, 0.7)"
                    color="white"
                    _hover={{ bg: "rgba(0, 0, 0, 0.8)" }}
                    _active={{ bg: "rgba(0, 0, 0, 0.9)" }}
                    borderRadius="full"
                    px={4}
                    py={1}
                    h="32px"
                    fontSize="sm"
                    fontWeight="medium"
                    boxShadow="lg"
                    backdropFilter="blur(10px)"
                  >
                    <HStack spacing={1} align="center">
                      <QuestionIcon boxSize="14px" />
                      <Text>選び方ガイド</Text>
                    </HStack>
                  </Button>
                </Box>
              )}
              
              {/* モバイル用下部固定ボタン */}
              <Box
                position="fixed"
                bottom="20px"
                left="50%"
                transform="translateX(-50%)"
                zIndex={30}
                display={{ base: "block", md: "none" }}
              >
                <HStack spacing={4}>
                  {/* 概算ボタン */}
                  {!chatDisclosure.isOpen && (
                    <Button
                      as={RouterLink}
                      to={targetUserId ? `/admin/user/${targetUserId}/equipment/confirmed-summary` : '/equipment/confirmed-summary'}
                      bg="rgba(0, 0, 0, 0.7)"
                      color="white"
                      _hover={{ bg: "rgba(0, 0, 0, 0.8)" }}
                      _active={{ bg: "rgba(0, 0, 0, 0.9)" }}
                      borderRadius="full"
                      px={4}
                      py={2}
                      fontSize="sm"
                      fontWeight="medium"
                      boxShadow="lg"
                      backdropFilter="blur(10px)"
                    >
                      <VStack spacing={0} align="center">
                        <CheckCircleIcon boxSize="16px" />
                        <Text fontSize="xs">概算見積</Text>
                      </VStack>
                    </Button>
                  )}
                  
                  {/* チャットボタン */}
                  {!chatDisclosure.isOpen && (
                    <Button
                      onClick={chatDisclosure.onToggle}
                      bg="rgba(0, 0, 0, 0.7)"
                      color="white"
                      _hover={{ bg: "rgba(0, 0, 0, 0.8)" }}
                      _active={{ bg: "rgba(0, 0, 0, 0.9)" }}
                      borderRadius="full"
                      px={4}
                      py={2}
                      fontSize="sm"
                      fontWeight="medium"
                      boxShadow="lg"
                      backdropFilter="blur(10px)"
                    >
                      <VStack spacing={0} align="center">
                        <ChatIcon boxSize="16px" />
                        <Text fontSize="xs">チャットで相談</Text>
                      </VStack>
                    </Button>
                  )}
                </HStack>
              </Box>
              
              {/* 右側のチャット画面 */}
              <Box 
                w={chatDisclosure.isOpen ? {base: "100%", sm: "400px"} : "0"}
                position="fixed" // スクロールに合わせてついてくるように fixed に変更
                top="100px" // ヘッダー分の高さを考慮
                right="0" // 画面の右端に固定
                h="calc(100vh - 100px)"
                transition="width 0.3s ease-in-out"
                overflowY="auto" // 内容が多い場合にスクロール可能にする
                zIndex={10} // 他の要素より手前に表示
                bg="white"
                borderWidth="1px"
                boxShadow="lg" // 影を追加して視認性を向上
              >
                {/* チャットを閉じるボタン */}
                <IconButton
                  icon={<CloseIcon />}
                  onClick={chatDisclosure.onClose}
                  aria-label="チャットを閉じる"
                  size="sm"
                  variant="ghost"
                  color="gray.500"
                  _hover={{ bg: "gray.100" }}
                  position="absolute"
                  top="10px"
                  right="10px"
                  zIndex={20}
                  bg="white"
                  borderRadius="full"
                  boxShadow="sm"
                />
                <ChatBox
                  itemType={selectedEquipment}
                  targetUserId={targetUserId || currentUser.uid}
                  isOpen={chatDisclosure.isOpen}
                />
              </Box>
            </>
          )}
        </Flex>

        {selectedItem && (
          <SelectedEquipmentModal
            item={selectedItem}
            config={currentEquipmentConfig}
            onClose={closeModal}
            onPreferenceChange={handlePreferenceChange}
            onUserEquipmentChange={fetchUserEquipments}
            isTsubo={isTsubo}
            equipmentDefinitions={equipmentDefinitions}
            fixedMap={fixedMap}
            targetUserId={targetUserId}
            navigate={navigate}
            onEquipmentTypeChange={setSelectedEquipment}
          />
        )}

        {/* ユーザー設備登録モーダル */}
        <UserEquipmentModal
          isOpen={userModalDisclosure.isOpen}
          onClose={userModalDisclosure.onClose}
          onRegister={handleRegisterEquipment}
          config={currentEquipmentConfig}
          targetUserId={targetUserId || currentUser.uid}
          isTsubo={isTsubo}
        />

        {/* 確認ダイアログ */}
        <AlertDialog
          isOpen={isConfirmDialogOpen}
          leastDestructiveRef={confirmDialogCancelRef}
          onClose={handleConfirmDialogNo}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                確認
              </AlertDialogHeader>

              <AlertDialogBody>
                {confirmDialogMessage}
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button ref={confirmDialogCancelRef} onClick={handleConfirmDialogNo}>
                  いいえ
                </Button>
                <Button colorScheme="gray" bg="black" color="white" onClick={handleConfirmDialogYes} ml={3}>
                  はい
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>

        {/* 選択中アイテムモーダル */}
        <SelectedItemsModal
          isOpen={showCartModal}
          onClose={() => setShowCartModal(false)}
          fixedItems={fixedItems}
          currentEquipmentConfig={currentEquipmentConfig}
          handlePreferenceChange={handlePreferenceChange}
          handleItemSelect={handleItemSelect}
          loadItemsAndPreferences={loadItemsAndPreferences}
          preferences={preferences}
          fetchUserEquipments={fetchUserEquipments}
          targetUserId={targetUserId}
          currentUser={currentUser}
          isTsubo={isTsubo}
          loading={loading}
          fixedTotalPrice={fixedTotalPrice}
        />

        {/* ガイドモーダル */}
        <GuideModal
          isOpen={showGuideModal}
          onClose={() => setShowGuideModal(false)}
          selectedEquipment={selectedEquipment}
          currentEquipmentConfig={currentEquipmentConfig}
        />
      </Box>
    </Flex>
  );
} 